import { z } from 'zod';

const MIN_EXAM_DURATION_TIME = 15 * 60; // 15 minutes, in seconds unit
const MAX_SESSION_TIME = 12 * 60 * 60; // 12 hrs, in seconds unit
const MAX_EXAM_DURATION_TIME = 24 * 60 * 60; // 24 hrs, in seconds unit

const BasePreExamSettingsSectionFormSchema = z.object({
  examDuration: z.object({
    type: z.enum(['single-session', 'multiple-session']).optional(),
    singleSessionValue: z.number().optional(),
    multipleSessionValue: z.array(
      z.object({
        value: z.number().optional(),
      }),
    ),
  }),
});

function addValidationIssue(ctx: z.RefinementCtx, path: (string | number)[], message: string) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path,
    message,
  });
}

// Validate require field

function validateRequiredField(
  data: z.infer<typeof BasePreExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  const { type } = data.examDuration;

  if (!type) {
    addValidationIssue(ctx, ['examDuration', 'type'], 'กรุณาเลือกประเภทระยะเวลาสอบ');
  }
}

// Validate exam duration time limit (Single session type)
function validateExamDurationSingleSessionTime(
  data: z.infer<typeof BasePreExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  const { type, singleSessionValue } = data.examDuration;

  if (type === 'single-session') {
    if (singleSessionValue === undefined) {
      addValidationIssue(ctx, ['examDuration', 'singleSessionValue'], 'กรุณากำหนดระยะเวลาสอบ');
    } else if (singleSessionValue < MIN_EXAM_DURATION_TIME) {
      addValidationIssue(
        ctx,
        ['examDuration', 'singleSessionValue'],
        'ระยะเวลาสอบต้องมีความยาวอย่างน้อย 15 นาที',
      );
    } else if (singleSessionValue > MAX_EXAM_DURATION_TIME) {
      addValidationIssue(
        ctx,
        ['examDuration', 'singleSessionValue'],
        'ระยะเวลาสอบรวมทั้งหมดต้องมีความยาวไม่เกิน 24 ชั่วโมง',
      );
    }
  }
}

// Validate exam duration time limit (Multiple session type)
function validateExamDurationMultipleSessionTime(
  data: z.infer<typeof BasePreExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  const { type, multipleSessionValue } = data.examDuration;

  if (type === 'multiple-session') {
    const totalSessionsTime = multipleSessionValue.reduce(
      (acc, session) => acc + (session.value ?? 0),
      0,
    );

    if (totalSessionsTime > MAX_EXAM_DURATION_TIME) {
      addValidationIssue(
        ctx,
        ['examDuration', 'multipleSessionValue', 'root'],
        'ระยะเวลาสอบรวมทั้งหมดต้องมีความยาวไม่เกิน 24 ชั่วโมง',
      );
    }

    for (let index = 0; index < multipleSessionValue.length; index++) {
      const session = multipleSessionValue[index];
      if (session.value === undefined) {
        addValidationIssue(
          ctx,
          ['examDuration', 'multipleSessionValue', index, 'value'],
          'กรุณากำหนดระยะเวลาสอบ',
        );
      } else if (session.value < MIN_EXAM_DURATION_TIME) {
        addValidationIssue(
          ctx,
          ['examDuration', 'multipleSessionValue', index, 'value'],
          'ระยะเวลาสอบต้องมีความยาวอย่างน้อย 15 นาที',
        );
      } else if (session.value > MAX_SESSION_TIME) {
        addValidationIssue(
          ctx,
          ['examDuration', 'multipleSessionValue', index, 'value'],
          'ระยะเวลาสอบต้องมีความยาวไม่เกิน 12 ชั่วโมง',
        );
      } else if (totalSessionsTime > MAX_EXAM_DURATION_TIME) {
        addValidationIssue(ctx, ['examDuration', 'multipleSessionValue', index, 'value'], '');
      }
    }
  }
}

export const PreExamSettingsSectionFormSchema = BasePreExamSettingsSectionFormSchema.superRefine(
  (data, ctx) => {
    validateRequiredField(data, ctx);
    validateExamDurationSingleSessionTime(data, ctx);
    validateExamDurationMultipleSessionTime(data, ctx);
  },
);
